import { ILanguageExtended } from "@multiweb/types/contracts";
import { CalendarGrid } from "UI/UI.Controls/CalendarGrid";
import { CalendarDataRow } from "UI/UI.Controls/DataGridSubcontrols/Components/CalendarDataRow";
import { IDataRowElement } from "UI/UI.Controls/DataGridSubcontrols/Components/IDataRow";
import { InvalidArgumentException, InvalidOperationException } from "../../../../Global/Base/Exceptions";
import { DateUtils } from "../../../../Global/Base/Utils/DateUtils";
import { OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { ActionType, IAction } from "../../../UI.Controls/Actions/IAction";
import { MenuType } from "../../../UI.Controls/Actions/MenuType";
import { DataRow } from "../../../UI.Controls/DataGridSubcontrols/Components/DataRow";
import { DropDownMenu } from "../../../UI.Controls/DropDownMenu/DropDownMenu";
import { DropDownMenuItemFlag } from "../../../UI.Controls/DropDownMenu/DropDownMenuItemFlag";
import { ICalendarTooltip, ICalendarTooltipCreator } from "../../../UI.Controls/ICalendarTooltip";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { GenericControl } from "../Controls/Control";
import { IContentBuilder } from "../DataBuilders/IContentBuilder";
import { ShowInsertAction } from "../XGControls/Actions/ShowInsertAction";
import { StandardAction } from "../XGControls/Actions/StandardAction";
import { CalendarFrame } from "../XGControls/Frames/CalendarFrame";
import { IMasterableFrameWithDataGrid } from "../XGControls/Frames/FrameInterfaces";
import { FrameSharedMethods } from "../XGControls/Frames/FrameSharedMethods";
import { ICalendarFrame } from "../XGControls/Frames/IDetailMasterableFrame";
import { IFrame } from "../XGControls/IFrame";
import { FormUICustomProperty, IFormUI, MenuTypes } from "./FormUI";
import { MenuUIStandard } from "./MenuUIStandard";
import { ObjectActionsMenu } from "./ObjectActionsMenu";

export class MenuUIStandardCalendar extends MenuUIStandard implements ICalendarTooltipCreator {
    private _textDateBuilder: IContentBuilder;
    private _newRecordAction: StandardAction;
    private _insertActions: IAction[] = [];
    private _visibleTooltips: ICalendarTooltip[] = [];
    private _lastTooltipOpeningMenu: ICalendarTooltip;
    private _textDateElement: HTMLElement | undefined;
    private __enabled: boolean = true;

    private _lastContextMenuInitiatilizator: HTMLElement;

    // @ts-ignore // nečteno-ponecháno
    private get _enabled(): boolean {
        return this.__enabled;
    }

    private set _enabled(value: boolean) {
        this.__enabled = value;
    }

    /**
    * Vrátí poslední zobrazený tooltip
    */
    private _getActiveTooltip(): ICalendarTooltip | undefined {
        return this._visibleTooltips.length > 0 ? this._visibleTooltips[this._visibleTooltips.length - 1] : undefined;
    }
    public override isVisible;
    constructor(formUI: IFormUI, frameElement: HTMLElement, isVisible = false, ctrlID: string, frame: IFrame | undefined, getLanguages: () => Promise<readonly ILanguageExtended[]>) {
        super(formUI, frameElement, isVisible, ctrlID, frame, getLanguages);
        this.isVisible = isVisible;
        this.element.classList.add("calendar-frame-toolbar");
        this._objectActionsMenu.opened.add(this._threeDotsMenuOpened.bind(this));
        this._objectActionsMenu.closed.add(this._threeDotsMenuClosed.bind(this));
        this._objectActionsMenu.itemSelected.add(() => this._closeAllTooltips());
    }

    public override addAction<T extends IAction>(action: T): T {
        if (!action) {
            throw new InvalidArgumentException("Invalid action argument");
        }

        switch (action.actionType) {
            case ActionType.standardPreviousInterval:
            case ActionType.standardNextInterval:
            case ActionType.standardSetToday:
                action.toMenu = MenuType.mtRecordNavigation;
                break;
            case ActionType.standardSwitchIntervalDay:
            case ActionType.standardSwitchIntervalMonth:
            case ActionType.standardSwitchIntervalWeek:
            //case ActionType.standardSwitchIntervalYear:
            // falls through
                action.toMenu = MenuType.mtFormControl;
                break;
            default:

                if (action.toMenu == MenuType.mtRecordNavigation ||
                    action.toMenu == MenuType.mtFormControl)
                    action.toMenu = MenuType.mtNone;
                return super.addAction(action);
        }

        return super.addAction(action);
    }

    protected override _addActionToMasterNode<T extends IAction>(action: T, masterNode: GenericControl, menuType: MenuTypes): boolean {
        if (action.actionType === ActionType.insert) {
            this._insertActions.push(action); // všechny insertovací akce se zapamatují, ale nepřidávají se přímo do toolbaru

            // místo nich se tam vloží zástupná akce s tlačítkem "+"
            if (this._newRecordAction == null) {
                this._newRecordAction = FrameSharedMethods.createStandardAction(ActionType.standardNewRecord, () => this._newRecordClicked(), this._frame);
                this.addAction(this._newRecordAction);
            }

            action.visible = true;
            action.visibilityChanged.add(() => this._updateNewRecordVisibility()); // TODO-rites: ?
            action.enabledChanged.add(() => this._updateNewRecordEnabled()); // TODO-rites: ?
            this._updateNewRecordVisibility();
            this._updateNewRecordEnabled();
            return false;
        }

        return super._addActionToMasterNode(action, masterNode, menuType);
    }

    private _updateNewRecordVisibility(): void {
        this._newRecordAction.visible = this._insertActions.some(a => a.visible);
    }

    private _updateNewRecordEnabled(): void {
        this._newRecordAction.enabled = this._insertActions.some(a => a.enabled);
    }

    private async _newRecordClicked(): Promise<void> {
        if (this._insertActions.length === 0) // tlačítko by bez akcí nemělo být vůbec přidané
            throw new InvalidOperationException("[MenuUIStandardCalendar] No insert actions for New button!");

        // při kliknutí na tlačítko plus se mohou stát dvě věci:

        // a) pokud existuje jen jedna akce, tak se hned spustí
        if (this._insertActions.length === 1) {
            await this._insertActions[0].execute();
            return;
        }

        // b) pokud existuje akcí víc, tak se zobrazí v dropdownu
        const dropDownMenu = new DropDownMenu();
        dropDownMenu.classList.add("form-ui-standard", "calendar-new-record-menu");

        for (const action of this._insertActions) {
            if (!action.visible)
                continue;

            const item = DropDownMenuItemFlag.createDropDownMenuItemFlag(
                () => action.execute(),
                action.locKey,
                action instanceof ShowInsertAction ? action.color : null,
                action.enabled,
                undefined,
                action instanceof ShowInsertAction ? action.locParams : undefined);

            dropDownMenu.addItem(item);
        }

        dropDownMenu.show(this._newRecordAction.toolbarButton.element, null);
    }

    /**
     * Umožňuje nastavit vlastní specifické property, na které mohou různé implementace reagovat
     * @param propertyName Název property - buď enum FormUICustomProperty nebo vlastní libovolný string
     * @param newValue Nastavovaná hodnota
     */
    public override setProperty(propertyName: FormUICustomProperty | string, newValue: any): void {
        if (typeof propertyName === "string")
            propertyName = propertyName.toLowerCase();

        switch (propertyName) {
            case "datetext":
                this._textDateBuilder = newValue;
                this._addTextDate();
                break;
            case "enabled":
                this._updateTextDateEnabledState(!!newValue);
                break;
            case "loading":
                this._setLoadingState(!!newValue);
                break;
            default:
        }
    }

    private async _addTextDate(): Promise<void> {
        if (!this._textDateBuilder)
            return;

        this._textDateElement = await this._textDateBuilder.build();
        this._actionContainer.element.querySelector("[data-tomenu='mtRecordNavigation']").insertAdjacentElement("beforeend", this._textDateElement);
    }

    private _setLoadingState(value: boolean): void {
        if (value) {
            this.showMenuLoader();
        } else {
            this.hideMenuLoader();
        }
    }

    /** Vytvoří UI reprezentaci tooltipu pro položku kalendáře.
     * @param tooltip Komponenta tooltipu, pro kterou se má vytvořit UI reprezentace.
     */
    public async createCalendarTooltip(tooltip: ICalendarTooltip): Promise<HTMLElement> {
        const content = createElement({ classes: "container form-ui-standard" });

        //leva cast - data
        const body = createElement({ classes: "body" });
        body.appendChild(createElement({ classes: "caption", value: tooltip.calendarItemData.caption }));

        if (tooltip.calendarItemData.dateFrom.toDateString() === tooltip.calendarItemData.dateTo.toDateString()) { //stejne datum od-do - zobrazí se jen 1x, pokud je include time, tak na druhém řádku bude čas od-do
            body.appendChild(createElement({ classes: "date single-day", value: DateUtils.dateToString(tooltip.calendarItemData.dateFrom, "d") }));
            if (tooltip.calendarItemData.includeTime)
                body.appendChild(createElement({ classes: "date time-range", value: `${DateUtils.dateToString(tooltip.calendarItemData.dateFrom, "t")} - ${DateUtils.dateToString(tooltip.calendarItemData.dateTo, "t")}` }));
        } else {
            body.appendChild(createElement({ classes: "date date-from", value: DateUtils.dateToString(tooltip.calendarItemData.dateFrom, "g") }));
            body.appendChild(createElement({ classes: "date date-to", value: DateUtils.dateToString(tooltip.calendarItemData.dateTo, "g") }));
        }

        //text fields
        if (tooltip.calendarItemData.textFields && tooltip.calendarItemData.textFields.length > 0) {
            const fields = createElement({ classes: "text-fields" });
            for (let i = 0; i < tooltip.calendarItemData.textFields.length; i++) {
                fields.appendChild(createElement({ value: tooltip.calendarItemData.textFieldsValues[i], attrs: { "data-field": tooltip.calendarItemData.textFields[i] } }));
            }
            body.appendChild(fields);
        }

        content.appendChild(body);

        //prava cast - akce
        const actionPlaceHolder = createElement({ classes: "actions" });
        if (tooltip.calendarItemData.showFormID && this._frame instanceof CalendarFrame) {
            const frame = this._frame as CalendarFrame;
            //akce detail
            this.addAction(frame.createStandardRecordAction(ActionType.standardOpenDetail, async (e: Event, dataKey: string) => {
                await frame.openDetail(e, dataKey);
                this._closeAllTooltips();
            }));
        }

        this._checkTooltipActionRights(tooltip);

        actionPlaceHolder.appendChild(this.renderRecordControls(() => tooltip.calendarItemData.dataKey));
        content.appendChild(actionPlaceHolder);

        tooltip.opened.add(this._tooltipOpenedHandler.bind(this));
        tooltip.closed.add(this._tooltipClosedHandler.bind(this));

        return content;
    }

    private _closeAllTooltips(): void {
        this._visibleTooltips.forEach(t => t.release(true));
    }

    private _checkTooltipActionRights(tooltip: ICalendarTooltip): void {
        //nastaví viditelnost akce editace
        const action = this.getAction(ActionType.standardEditRecord);
        if (action)
            action.visible = tooltip.calendarItemData.showFormID && this._frame instanceof CalendarFrame && !this._frame.hideButtons.edit;
    }

    private _tooltipOpenedHandler(tooltip: ICalendarTooltip): void {
        this._visibleTooltips.push(tooltip);
    }

    private _tooltipClosedHandler(tooltip: ICalendarTooltip): void {
        this._visibleTooltips = this._visibleTooltips.filter(t => t !== tooltip);
    }

    private _threeDotsMenuOpened(_menu: ObjectActionsMenu, _ao: OWObject): void {
        this._lastTooltipOpeningMenu = this._getActiveTooltip();

        if (this._lastTooltipOpeningMenu)
            this._lastTooltipOpeningMenu.block(); //Pokud se otevře "..." menu, tak se blokne tooltip, aby se nezavřel, pokud kurzor myši ujede mimo.
    }

    private _threeDotsMenuClosed(_menu: ObjectActionsMenu): void {
        this._visibleTooltips.forEach(t => {
            if (!t.isMouseOver)
                t.release();
            else
                t.isBlocked = false;
        });
    }

    protected _checkRowIsSelected(row: DataRow): boolean {
        if (this._frame && this._frame instanceof ICalendarFrame)
            row.isSelected = row.isSelected && row.dataKey === this._frame.actualObjectComplexKey;
        return row.isSelected;
    }

    /*
     * Vrátí element ke kterému má být přilepeno otevírané kontextové menu,
     * V případě CalendarFrame vrací poslední kliknutý element v tooltipu. Akce "..." je v plovoucím tooltipu.
     * Navíc může být přes více akcí a není jednoznačně určitelné, který tooltip akci vyvolal.
     * CalendarFrame nepoužívá auto-refresh, proto to nevadí. V případě auto-refreshe by poslední kliknutý element už nemusel existovat v DOMu
     */
    protected override _getContextMenuAnchorElement(_dataKey: string, _row?: IDataRowElement, _e?: Event): HTMLElement {
        return this._lastContextMenuInitiatilizator;
    }

    protected override _checkContextMenuReady(_frame: IFrame, dataKey: string): void {
        if (dataKey && this._frame instanceof IMasterableFrameWithDataGrid && this._frame.currentDataGrid instanceof CalendarGrid) {
            const complexDataKey = CalendarDataRow.getDataKey(this._frame.currentDataGrid.uniqueDSId, dataKey);

            if (this._pendingRightsEvaluationObjId && this._pendingRightsEvaluationObjId == complexDataKey) {
                this._openThreeDotsOnSelectedRow(complexDataKey, undefined, undefined, true);
                this._pendingRightsEvaluationObjId = undefined;
            }
        }
    }
    protected override async _showRowActionMenu(e: Event, dataKey: string): Promise<void> {
        this._lastContextMenuInitiatilizator = e && e.target as HTMLElement || undefined;
        super._showRowActionMenu(e, dataKey);
    }
    protected override _openThreeDotsOnSelectedRow(dataKey: string, row?: IDataRowElement, e?: Event, forceRedraw?: boolean): void {
        //zkontroluje jestli kontextové menu bylo vyvoláno z jiného tooltipu stejného objektu (např. více denní položka)
        if (this._objectActionsMenu.isShown && this._lastTooltipOpeningMenu && this._lastTooltipOpeningMenu !== this._getActiveTooltip()) {
            const anchorElement = this._getContextMenuAnchorElement(dataKey, row);
            this._lastTooltipOpeningMenu.release();
            this._objectActionsMenu.show(anchorElement, e, this._frame.actualObject, false, this._frame);
        } else
            super._openThreeDotsOnSelectedRow(dataKey, row, e, forceRedraw);
    }

    protected override _openThreeDotsWithCheckChanges(dataKey: string, row?: IDataRowElement, e?: Event): void {
        if (this._lastTooltipOpeningMenu)
            this._lastTooltipOpeningMenu.release();
        const activeTooltip = this._getActiveTooltip();
        if (activeTooltip) //využíváno i na detailu v denním zobrazení a ten je bez tooltipu
            activeTooltip.block();
        super._openThreeDotsWithCheckChanges(dataKey, row, e);
    }

    private _updateTextDateEnabledState(enabled: boolean): void {
        if (this._textDateElement)
            this._textDateElement.classList.toggle("disabled", !enabled);
    }
}
